import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Check, Loader2, Plus, Sparkles, X, AlertCircle } from 'lucide-react'
import { useSchoolAddGrade, useUserInfo, useNationalExams, useNationalExamCourses, useSchoolExamQuestionAdd, useTags } from '../../react-query/queries'
import { useAuth } from '../../utils/Auth/AuthProvider'
import { Button, EmptyState, FormField, PageHeader, PagedFooter, PanelCard, Segmented, Spinner, Switch } from '../../school_panel/components/ui'
import { usePagination } from '../../school_panel/lib/usePagination'
import { fieldClass, NAME_MAX } from '../../school_panel/lib/fieldClass'
import ExamQuestionAddCard from '../../components/Exams/ExamQuestionAddCard'
import ExamProcessingPipeline from '../../components/Exams/ExamProcessingPipeline'
import { ExamSessionProvider, useExamSessions } from '../../store/ExamSessionStore'
import { saveToExams } from '../../components/Exams/examReview'
import { adminGetAllFields, adminGetInstitutionFields, adminGetFieldAcademicYears, adminGetAcademicYearCourses, AdminField, AdminAcademicYear, submitNationalExamAddExam } from '../../utils/remote'
import { LINK_BTN } from '../../school_panel/components/ui/linkButton'
import { canUseMixedExam } from '../../school_panel/lib/mixedExam'
import { UniversityChoice, HUB_NAME } from '../../components/Exams/UniCoursePicker'

const MIX_INPUT = fieldClass
// Design `.opt` / `.opt.on`: pill options, navy fill when chosen.
const tileClass = (selected: boolean) =>
    `inline-flex min-h-[36px] max-w-full items-center gap-1.5 rounded-full border px-[13px] py-1 text-left text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${selected ? 'border-navy bg-navy text-white' : 'border-line-strong bg-surface text-ink hover:border-slate'}`

const TILE_GRID = 'flex flex-wrap gap-2'

/** A pill group that pages at 10 so hundreds of courses or fields never form a wall. */
function PagedTiles<T>({ items, label, render, resetKey }: { items: T[]; label: string; render: (item: T) => React.ReactNode; resetKey?: unknown }) {
    const pg = usePagination(items, 10, resetKey)
    return (
        <div>
            <div role="group" aria-label={label} className={TILE_GRID}>
                {pg.pageItems.map(render)}
            </div>
            <PagedFooter className="mt-3" from={pg.from} to={pg.to} total={pg.total} page={pg.page} pageCount={pg.pageCount} onPage={pg.setPage} label={`${label} pagination`} />
        </div>
    )
}

/** An on/off switch row in the artboard style (`.trow`); the visible label names the control. */
const MixSwitch: React.FC<{ checked: boolean; onChange: () => void; label: string; hint?: string; bare?: boolean }> = ({ checked, onChange, label, hint, bare }) => {
    const id = `mx-${label.replace(/\s+/g, '-').toLowerCase()}`
    const text = (
        <span className="min-w-0">
            <span id={id} className="block font-semibold text-ink">{label}</span>
            {hint && <span className="block text-xs text-muted">{hint}</span>}
        </span>
    )
    const sw = <Switch tone="navy" checked={checked} onChange={() => onChange()} label={label} labelledBy={id} />
    // Bare: the Scope card's own row, switch first then text.
    return bare
        ? <div className="flex items-center gap-3.5">{sw}{text}</div>
        : <div className="flex min-h-[44px] items-center justify-between gap-4 rounded-[14px] border border-line bg-surface-2 px-3.5 py-2">{text}{sw}</div>
}

/** Card for one builder step: mono "Step N" kicker over the title. */
const StepCard: React.FC<{ n: number; title: string; aside?: React.ReactNode; children: React.ReactNode }> = ({ n, title, aside, children }) => (
    <PanelCard
        title={
            <div>
                <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Step {n}</p>
                <h2 className="mt-1 font-display text-[17px] font-bold tracking-[-0.01em] text-ink">{title}</h2>
            </div>
        }
        actions={aside}
    >
        {children}
    </PanelCard>
)

// Session tab bar
const SessionTabs: React.FC = () => {
    const { state, addSession, removeSession, setActive } = useExamSessions()
    const statusIcon = (status: string) => {
        if (status === 'done') return <Check size={14} strokeWidth={1.8} aria-hidden />
        if (status === 'error') return <AlertCircle size={14} strokeWidth={1.8} aria-hidden />
        if (status === 'extracting' || status === 'processing') return <Loader2 size={14} strokeWidth={1.8} className="animate-spin" aria-hidden />
        return <span aria-hidden className="h-2 w-2 rounded-full bg-current" />
    }

    return (
        <div className="mb-[18px] flex flex-wrap items-center gap-2">
            {state.sessions.map(s => {
                const active = s.id === state.activeId
                const showPct = s.pipelinePhase && s.pipelinePhase !== 'idle' && s.pipelinePhase !== 'done' && s.pipelineTotal > 0
                return (
                    <div
                        key={s.id}
                        className={`inline-flex min-h-[40px] items-center rounded-full border text-[13px] font-semibold transition-colors ${
                            active
                                ? 'border-navy bg-navy text-white dark:border-mist-fixed dark:bg-mist-fixed dark:text-navy'
                                : 'border-line-strong bg-surface text-muted-2 hover:text-ink'}`}
                    >
                        <button
                            type="button"
                            aria-pressed={active}
                            onClick={() => setActive(s.id)}
                            className="inline-flex min-h-[40px] items-center gap-1.5 whitespace-nowrap rounded-full pl-3.5 pr-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                        >
                            {statusIcon(s.status)}
                            <span className="max-w-[140px] truncate">{s.formdata.name || s.name}</span>
                            {showPct && (
                                <span className="font-mono text-xs opacity-80">{Math.round((s.pipelineCompleted / s.pipelineTotal) * 100)}%</span>
                            )}
                        </button>
                        {state.sessions.length > 1 && (
                            <button
                                type="button"
                                aria-label={`Close ${s.formdata.name || s.name}`}
                                onClick={() => removeSession(s.id)}
                                className="mr-1.5 grid h-7 w-7 place-items-center rounded-full opacity-70 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                            >
                                <X size={14} strokeWidth={1.8} aria-hidden />
                            </button>
                        )}
                    </div>
                )
            })}
            <button
                type="button"
                onClick={() => addSession()}
                className="inline-flex min-h-[40px] items-center gap-1.5 rounded-full border border-dashed border-line-strong px-3.5 text-[13px] font-semibold text-muted-2 transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
                <Plus size={14} strokeWidth={1.8} aria-hidden /> New
            </button>
        </div>
    )
}

const MixedExamInner: React.FC<{ sessionId: string }> = ({ sessionId }) => {
    const { token, role } = useAuth()
    const { data: userData } = useUserInfo()
    const { state, updateSession, removeSession } = useExamSessions()

    const s = state.sessions.find(sess => sess.id === sessionId) || null
    if (!s) return <div className="text-center py-12 text-body">No active session</div>

    const updateThis = (patch: Partial<typeof s>) => updateSession(sessionId, patch)

    // Derived state from session
    const step = s.step
    const gradeStateId = s.gradeId
    const subjectStateId = s.subjectId
    const selectedCourses = s.selectedCourses
    const formdata = s.formdata
    const isNational = s.isNational
    const nationalExamMode = s.nationalExamMode
    const selectedNationalExam = s.selectedNationalExam
    const tagId = s.tagId
    const academicYearId = s.academicYearId
    const createdExamId = s.createdExamId
    const createdExamIds = s.createdExamIds
    const pendingQuestions = s.pendingQuestions
    const questionsExtracted = s.questionsExtracted

    const [isAdding, setISAdding] = useState(false)
    const savedExamIds = useRef(new Set<string>())
    // Partial-save warnings from every attempt, so a later throw or retry does not lose them.
    const partialWarnings = useRef<string[]>([])
    // The exam detail tab opens once; a later Save after a partial-only save must not open it again.
    const detailOpened = useRef(false)
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [isExtractingQuestions, setIsExtractingQuestions] = useState(false)
    const [extractQuestionsFn, setExtractQuestionsFn] = useState<(() => void) | null>(null)
    const [proceedToProcessingFn, setProceedToProcessingFn] = useState<(() => void) | null>(null)

    const [fieldsData, setFieldsData] = useState<AdminField[] | null>(null)
    const [academicYears, setAcademicYears] = useState<AdminAcademicYear[] | null>(null)
    const [coursesData, setCoursesData] = useState<{ id: number; name: string }[] | null>(null)

    const { mutateAsync: schoolExamQuestionAdd } = useSchoolExamQuestionAdd()
    const { mutateAsync: schoolAddExam } = useSchoolAddGrade()

    // YADA Education Hub admins pick the students: school, or university and college.
    const isHubAdmin = !!userData?.is_hub_admin
    const targetLevel: 'school' | 'college_university' | undefined = isHubAdmin ? (s.level === 'uni' ? 'college_university' : 'school') : (() => {
        const raw = (userData?.institution_type ?? userData?.institution_name ?? '') as string
        const instType = String(raw || '').trim().toLowerCase()
        if (instType.includes('school')) return 'school'
        if (instType.includes('college') || instType.includes('university')) return 'college_university'
        const r = String(role ?? '').toLowerCase()
        if (r === 'school') return 'school'
        if (r.includes('college') || r.includes('university')) return 'college_university'
        return undefined
    })()

    const { status: nationalStatus, data: nationalData } = useNationalExams(nationalExamMode ? undefined : targetLevel, true)
    const { status: neCoursesStatus, data: neCoursesData } = useNationalExamCourses(nationalExamMode && selectedNationalExam !== '-1' ? selectedNationalExam : null)
    const { data: tagsData } = useTags()

    const selectedExamBrief = useMemo(
        () => (nationalData || []).find((n: any) => String(n.id) === selectedNationalExam) || null,
        [nationalData, selectedNationalExam]
    )
    const aiKnowledgeOnly = !!(
        nationalExamMode
        && selectedExamBrief
        && selectedExamBrief.requires_institution === false
        && selectedExamBrief.requires_department === false
    )

    // Setters that update session
    const setStep = (v: number) => updateThis({ step: v })
    const setGradeStateId = (v: string) => updateThis({ gradeId: v })
    const setSubjectStateId = (v: string) => updateThis({ subjectId: v })
    const setSelectedCourses = (fn: (prev: typeof selectedCourses) => typeof selectedCourses) => {
        const next = fn(selectedCourses)
        updateThis({ selectedCourses: next })
        try { localStorage.setItem('add_exam_selected_courses', JSON.stringify(next)) } catch {}
    }
    const setFormdata = (fn: (prev: typeof formdata) => typeof formdata) => updateThis({ formdata: fn(formdata) })
    const setAcademicYearId = (v: string) => updateThis({ academicYearId: v })
    const setNationalExamMode = (v: boolean) => updateThis({ nationalExamMode: v })
    const setSelectedNationalExam = (v: string) => updateThis({ selectedNationalExam: v })
    const setIsNational = (v: boolean) => updateThis({ isNational: v })
    const setTagId = (v: string) => updateThis({ tagId: v })

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = event.target
        setFormdata(prev => ({ ...prev, [name]: name === 'given_time' ? Number(value) : value }))
    }

    const isValidStep1 = () => nationalExamMode ? selectedNationalExam !== '-1' : gradeStateId !== '-1'
    const isValidStep2 = () => {
        if (nationalExamMode) return selectedCourses.length > 0
        if (targetLevel === 'college_university') return academicYearId !== '-1' && selectedCourses.length > 0
        return subjectStateId !== '-1'
    }
    const [showStep3Errors, setShowStep3Errors] = useState(false)
    const isValidStep3 = () => formdata.name.trim().length >= 3 && Number(formdata.given_time) > 0 && Number(formdata.given_time) <= 300

    // Load fields/years/courses for university
    useEffect(() => {
        let mounted = true
        ;(async () => {
            try {
                const instId = userData?.institution_id ?? (isHubAdmin ? 0 : null)
                if (targetLevel === 'college_university' && instId != null) {
                    const fields = isHubAdmin ? await adminGetAllFields() : await adminGetInstitutionFields(instId)
                    if (!mounted) return
                    setFieldsData(fields || [])
                } else { if (mounted) setFieldsData(null) }
            } catch { if (mounted) setFieldsData([]) }
        })()
        return () => { mounted = false }
    }, [targetLevel, userData, isHubAdmin])

    useEffect(() => {
        let mounted = true
        ;(async () => {
            try {
                const instId = userData?.institution_id ?? (isHubAdmin ? 0 : null)
                if (targetLevel === 'college_university' && instId != null && gradeStateId !== '-1') {
                    const years = await adminGetFieldAcademicYears(instId, Number(gradeStateId))
                    if (!mounted) return
                    setAcademicYears(years || [])
                    setAcademicYearId('-1')
                    setCoursesData(null)
                } else { if (mounted) setAcademicYears(null) }
            } catch { if (mounted) setAcademicYears([]) }
        })()
        return () => { mounted = false }
    }, [targetLevel, userData, gradeStateId])

    useEffect(() => {
        let mounted = true
        ;(async () => {
            try {
                const instId = userData?.institution_id ?? (isHubAdmin ? 0 : null)
                if (targetLevel === 'college_university' && instId != null && gradeStateId !== '-1' && academicYearId !== '-1') {
                    const courses = await adminGetAcademicYearCourses(instId, Number(gradeStateId), Number(academicYearId))
                    if (!mounted) return
                    setCoursesData(courses || [])
                } else { if (mounted) setCoursesData(null) }
            } catch { if (mounted) setCoursesData([]) }
        })()
        return () => { mounted = false }
    }, [targetLevel, userData, gradeStateId, academicYearId])

    const createExam = async () => {
        if (isAdding) return
        setSubmitError(null)
        if (!isValidStep3()) { setSubmitError('Enter an exam name (at least 3 characters) and a time between 1 and 300 minutes.'); return }
        setISAdding(true)
        try {
            if (nationalExamMode) {
                const courseId = selectedCourses.length > 0 ? Number(selectedCourses[0].id) : undefined
                const payload = { 
                    name: formdata.name.trim(), 
                    time: formdata.given_time, 
                    course: courseId, 
                    desc: formdata.description?.trim() || '',
                    tag_id: tagId !== '-1' ? Number(tagId) : undefined
                }
                const resp = await submitNationalExamAddExam(token, Number(selectedNationalExam), payload)
                if (resp?.done === 'done' && resp.exam_id) {
                    updateThis({ createdExamIds: [String(resp.exam_id)], createdExamId: String(resp.exam_id), step: 4 })
                } else { setSubmitError('The national exam could not be created. Try again.') }
                setISAdding(false)
                return
            }

            const created: string[] = []
            const targetCourseIds = targetLevel === 'college_university' ? selectedCourses.map(c => c.id) : (subjectStateId !== '-1' ? [subjectStateId] : [])
            if (!targetCourseIds.length) { setISAdding(false); setSubmitError('Select at least one course.'); return }

            if (targetLevel === 'college_university') {
                const postData = new FormData()
                postData.append('name', formdata.name.trim())
                postData.append('time', `${formdata.given_time}`)
                postData.append('desc', formdata.description?.trim() || '')
                postData.append('courses', JSON.stringify(targetCourseIds))
                if (academicYearId !== '-1') postData.append('academic_year', academicYearId)
                if (gradeStateId !== '-1') postData.append('field_of_study', gradeStateId)
                if (isHubAdmin && s.universityId != null) postData.append('university', String(s.universityId))
                if (isNational) {
                    postData.append('exam_scope', 'national')
                    if (selectedNationalExam !== '-1') postData.append('national_exam', selectedNationalExam)
                } else { postData.append('exam_scope', 'none') }
                if (tagId !== '-1') postData.append('tag_id', tagId)
                try {
                    const resp: any = await schoolAddExam({ toBeValidated: token, data: postData } as any)
                    if (resp && (resp['exam_id'] || resp['done'] === 'done')) {
                        const id = resp['exam_id'] || resp.id
                        if (id) created.push(String(id))
                    }
                } catch (err) { }
            } else {
                for (const cid of targetCourseIds) {
                    const postData = new FormData()
                    postData.append('name', formdata.name.trim())
                    postData.append('time', `${formdata.given_time}`)
                    postData.append('desc', formdata.description?.trim() || '')
                    if (gradeStateId !== '-1') postData.append('grade_id', gradeStateId)
                    if (cid !== '-1') postData.append('subject_id', cid)
                    if (isNational) {
                        postData.append('exam_scope', 'national')
                        if (selectedNationalExam !== '-1') postData.append('national_exam', selectedNationalExam)
                    } else { postData.append('exam_scope', 'none') }
                    if (tagId !== '-1') postData.append('tag_id', tagId)
                    try {
                        const resp: any = await schoolAddExam({ toBeValidated: token, data: postData } as any)
                        if (resp && (resp['exam_id'] || resp['done'] === 'done')) {
                            const id = resp['exam_id'] || resp.id
                            if (id) created.push(String(id))
                        }
                    } catch (err) { }
                }
            }

            if (created.length > 0) {
                try { localStorage.setItem('add_exam_grade', gradeStateId); localStorage.setItem('add_exam_selected_courses', JSON.stringify(selectedCourses)) } catch {}
                updateThis({ createdExamIds: created, createdExamId: created[0], step: 4 })
            } else { setSubmitError('No exams could be created. Check the details and try again.') }
        } catch (err) {
            
            setSubmitError('The exam could not be created. Try again.')
        } finally { setISAdding(false) }
    }

    const handleSaveQuestions = useCallback(async (qs: any[]) => {
        const examIdsToSave = (createdExamIds?.length) ? createdExamIds : (createdExamId ? [createdExamId] : [])
        const { failed, results } = await saveToExams({
            examIds: examIdsToSave, sent: qs.length, savedIds: savedExamIds.current, warnings: partialWarnings.current,
            post: (exId) => schoolExamQuestionAdd({ toBeValidated: token, data: qs as any, exam_id: exId }),
        })
        if (partialWarnings.current.length > 0) setSubmitError(partialWarnings.current.join(' '))
        if (failed.length > 0) {
            // Throw so the pipeline shows its save error; Save again only retries the exams that failed.
            throw new Error(`The questions could not be saved to ${failed.length === 1 ? 'one exam' : `${failed.length} exams`}. Try again.`)
        }
        if (partialWarnings.current.length > 0) {
            // Keep the page: removing the session now would hide the warning, and a retry would duplicate.
            updateThis({ status: 'done' })
            const first = examIdsToSave[0]
            if (first && !detailOpened.current) { detailOpened.current = true; window.open(`/exams/exam-detail/${first}`, '_blank') }
            return results
        }
        updateThis({ status: 'done' })
        // Open exam detail in new tab and remove this session
        const firstExamId = examIdsToSave[0]
        if (firstExamId && !detailOpened.current) {
            detailOpened.current = true
            window.open(`/exams/exam-detail/${firstExamId}`, '_blank')
        }
        removeSession(sessionId)
        return results
    }, [createdExamIds, createdExamId, schoolExamQuestionAdd, token, updateThis, removeSession, sessionId])

    const courseIds = useMemo(() => selectedCourses.map(c => c.id), [selectedCourses])

    const removeSelectedCourse = (courseId: string) => {
        setSelectedCourses(prev => {
            const next = prev.filter(c => c.id !== courseId)
            if (next.length > 0) updateThis({ subjectId: next[next.length - 1].id })
            else updateThis({ subjectId: '-1' })
            return next
        })
    }

    const renderFieldTile = (field: AdminField) => {
        const selected = String(field.id) === gradeStateId
        return (
            <button key={field.id} type="button" aria-pressed={selected}
                onClick={() => { setGradeStateId(String(field.id)); setAcademicYearId('-1'); updateThis({ subjectId: '-1' }); setCoursesData(null) }}
                className={tileClass(selected)}
            >
                <span className="line-clamp-2 break-words">{field.name}</span>
            </button>
        )
    }

    const renderAcademicYearTile = (year: AdminAcademicYear) => {
        const selected = String(year.id) === academicYearId
        return (
            <button key={year.id} type="button" aria-pressed={selected}
                onClick={() => { setAcademicYearId(String(year.id)); updateThis({ subjectId: '-1' }); setCoursesData(null) }}
                className={tileClass(selected)}
            >
                <span className="break-words">{year.name}</span>
            </button>
        )
    }

    const renderCourseTile = (course: { id: number; name: string }) => {
        const isSelected = selectedCourses.some(c => c.id === String(course.id))
        const yearName = academicYears?.find(y => String(y.id) === academicYearId)?.name || ''
        return (
            <button key={course.id} type="button" aria-pressed={isSelected}
                onClick={() => {
                    if (isSelected) { removeSelectedCourse(String(course.id)) }
                    else {
                        const next = [...selectedCourses, { id: String(course.id), name: course.name, academicYearId, academicYearName: yearName }]
                        updateThis({ selectedCourses: next, subjectId: String(course.id) })
                        try { localStorage.setItem('add_exam_selected_courses', JSON.stringify(next)) } catch {}
                    }
                }}
                className={tileClass(isSelected)}
            >
                <span className="line-clamp-2 break-words">{course.name}</span>
            </button>
        )
    }

    const blockerNote = (() => {
        if (step === 1 && !isValidStep1()) return nationalExamMode ? 'Choose a national exam to continue.' : targetLevel === 'college_university' ? 'Choose a field of study to continue.' : 'Choose a grade to continue.'
        if (step === 2 && !isValidStep2()) return nationalExamMode ? 'Select at least one course to continue.' : targetLevel === 'college_university' ? 'Select an academic year and at least one course.' : 'Choose a subject to continue.'
        return ''
    })()

    const levelLabel = targetLevel === 'college_university' ? 'College or university' : targetLevel === 'school' ? 'School' : 'Standard'
    const fieldName = targetLevel === 'college_university' && gradeStateId !== '-1'
        ? (fieldsData || []).find(f => String(f.id) === gradeStateId)?.name
        : undefined
    const timeValid = Number(formdata.given_time) > 0 && Number(formdata.given_time) <= 300
    const showCourses = nationalExamMode || targetLevel === 'college_university'

    const summaryRows: { label: string; value: string | undefined }[] = [
        { label: 'Mode', value: nationalExamMode ? 'National exam' : levelLabel },
        { label: 'National exam', value: selectedExamBrief ? String(selectedExamBrief.name) : undefined },
        ...(targetLevel === 'college_university' && !nationalExamMode ? [{ label: 'Field', value: fieldName }] : []),
        ...(showCourses ? [{ label: 'Courses', value: selectedCourses.length ? `${selectedCourses.length} selected` : undefined }] : []),
        ...(isHubAdmin && !nationalExamMode && targetLevel === 'college_university' ? [{ label: 'Students see', value: s.universityName || HUB_NAME }] : []),
        { label: 'Exam name', value: formdata.name.trim() || undefined },
        { label: 'Time', value: timeValid ? `${formdata.given_time} min` : undefined },
    ]

    const navButtons = (
        <>
            {step > 1 && step <= 3 && (
                <Button variant="ghost" onClick={() => setStep(step - 1)}>Back</Button>
            )}
            {step === 1 && (
                <Button disabled={!isValidStep1()} onClick={() => { setSubmitError(null); setStep(2) }}>Next<ArrowRight size={18} strokeWidth={1.8} aria-hidden /></Button>
            )}
            {step === 2 && (
                <Button disabled={!isValidStep2()} onClick={() => { setSubmitError(null); setStep(3) }}>Next<ArrowRight size={18} strokeWidth={1.8} aria-hidden /></Button>
            )}
            {step === 3 && (
                <Button loading={isAdding} disabled={isAdding} onClick={() => { setShowStep3Errors(true); createExam() }}>
                    {isAdding ? 'Creating…' : 'Create exam'}
                </Button>
            )}
            {step === 4 && !questionsExtracted && !pendingQuestions && (
                <>
                    <Button variant="ghost" onClick={() => setStep(3)}>Back</Button>
                    <Button onClick={() => { if (extractQuestionsFn) extractQuestionsFn() }} disabled={isExtractingQuestions} loading={isExtractingQuestions}>
                        {isExtractingQuestions ? 'Extracting…' : 'Extract questions'}
                    </Button>
                </>
            )}
            {step === 4 && questionsExtracted && !pendingQuestions && (
                <>
                    <Button variant="ghost" onClick={() => updateThis({ questionsExtracted: false })}>Back</Button>
                    <Button onClick={() => { if (proceedToProcessingFn) proceedToProcessingFn() }}>Next: process questions<ArrowRight size={18} strokeWidth={1.8} aria-hidden /></Button>
                </>
            )}
        </>
    )

    const nationalSelect = (
        <select aria-label="National exam" value={selectedNationalExam} onChange={e => setSelectedNationalExam(e.target.value)} className={MIX_INPUT}>
            <option value="-1">Choose a national exam</option>
            {(nationalData || []).map((n: any) => <option key={n.id} value={String(n.id)}>{n.name}</option>)}
        </select>
    )

    return (
        <div className="min-w-0">
            {(role === 'superuser' || isHubAdmin) && (
                <section aria-label="Exam scope" className="mb-[18px] flex flex-wrap items-center gap-x-7 gap-y-4 rounded-[20px] border border-line bg-surface px-5 py-4 shadow-card">
                    {isHubAdmin && !nationalExamMode && (
                        <div className="flex items-center gap-3">
                            <span className="text-[13px] font-semibold text-ink">Students</span>
                            <Segmented<'school' | 'uni'>
                                label="Students"
                                dense
                                options={[{ key: 'school', label: 'School' }, { key: 'uni', label: 'University & college' }]}
                                value={s.level === 'uni' ? 'uni' : 'school'}
                                onChange={(v) => {
                                    updateThis({ level: v, step: 1, gradeId: '-1', subjectId: '-1', selectedCourses: [], academicYearId: '-1' })
                                    setCoursesData(null); setAcademicYears(null); setSubmitError(null)
                                }}
                            />
                        </div>
                    )}
                    {role === 'superuser' && (
                    <MixSwitch
                        bare
                        checked={nationalExamMode}
                        label="National exam mode"
                        hint="Build the exam from a national exam's courses"
                        onChange={() => {
                            const next = !nationalExamMode
                            updateThis({
                                nationalExamMode: next, step: 1, gradeId: '-1', subjectId: '-1',
                                selectedNationalExam: '-1', selectedCourses: [], academicYearId: '-1',
                                isNational: next,
                            })
                            setCoursesData(null); setSubmitError(null)
                        }}
                    />
                    )}
                </section>
            )}

            {submitError && (
                <p role="alert" className="mb-[18px] rounded-[12px] border border-danger/30 bg-danger-bg px-3.5 py-2.5 text-sm text-danger-ink">{submitError}</p>
            )}

            <div className={step === 4 ? 'min-w-0' : 'grid grid-cols-1 items-start gap-[18px] xl:grid-cols-12'}>
                <div className={step === 4 ? 'min-w-0' : 'min-w-0 xl:col-span-8'}>
                    {/* Step 1: Scope */}
                    {step === 1 && (
                        <StepCard n={1} title={nationalExamMode ? 'Pick a national exam' : targetLevel === 'college_university' ? 'Pick a field of study' : 'Pick a grade'}>
                            {nationalExamMode ? (
                                nationalSelect
                            ) : targetLevel === 'college_university' ? (
                                fieldsData
                                    ? <PagedTiles items={fieldsData} label="Fields of study" render={renderFieldTile} />
                                    : <div className="flex items-center gap-2 py-4 text-sm text-muted"><Spinner size="sm" /> Loading…</div>
                            ) : (
                                // School students: a mixed exam is built from a national exam's courses.
                                <p className="py-4 text-sm text-muted">
                                    {role === 'superuser'
                                        ? 'For school students, turn on National exam mode above and pick a national exam.'
                                        : <>Mixed exams for school students are built from national exams by YADA. To write an exam for your students, use <Link to="/exams/add-exam" className="font-semibold text-ink underline">Create an exam</Link>.</>}
                                </p>
                            )}
                        </StepCard>
                    )}

                    {/* Step 2: Sources */}
                    {step === 2 && (
                        <StepCard
                            n={2}
                            title={nationalExamMode ? 'Pick courses' : targetLevel === 'college_university' ? 'Pick an academic year and courses' : 'Pick a subject'}
                            aside={showCourses ? <span className="text-[13px] text-muted">{selectedCourses.length} selected</span> : undefined}
                        >
                            {nationalExamMode ? (
                                neCoursesStatus === 'success' && neCoursesData ? (
                                    <PagedTiles items={(neCoursesData?.courses || []) as any[]} label="Courses" render={(c: any) => {
                                            const isSelected = selectedCourses.some(sc => sc.id === String(c.id))
                                            return (
                                                <button key={c.id} type="button" aria-pressed={isSelected}
                                                    onClick={() => {
                                                        if (isSelected) removeSelectedCourse(String(c.id))
                                                        else {
                                                            const next = [...selectedCourses, { id: String(c.id), name: c.name, academicYearId: '', academicYearName: '' }]
                                                            updateThis({ selectedCourses: next, subjectId: String(c.id) })
                                                        }
                                                    }}
                                                    className={tileClass(isSelected)}
                                                >
                                                    <span className="break-words">{c.name}</span>
                                                </button>
                                            )
                                        }} />
                                ) : <div className="flex items-center gap-2 py-4 text-sm text-muted"><Spinner size="sm" /> Loading courses…</div>
                            ) : targetLevel === 'college_university' ? (
                                <div className="space-y-6">
                                    {/* Selected courses */}
                                    {selectedCourses.length > 0 && (
                                        <div className="flex flex-wrap gap-2">
                                            {selectedCourses.map(c => (
                                                <span key={c.id} className="inline-flex min-h-[36px] items-center gap-1.5 rounded-full bg-mist pl-3 pr-1 text-[13px] font-semibold text-ink">
                                                    <span>{c.name}</span>
                                                    <button type="button" aria-label={`Remove ${c.name}`} onClick={() => removeSelectedCourse(c.id)} className="grid h-8 w-8 place-items-center rounded-full text-danger hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"><X size={14} strokeWidth={1.8} aria-hidden /></button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    {/* Academic years */}
                                    <div>
                                        <h3 className="mb-2.5 text-[13px] font-semibold text-ink">Academic year</h3>
                                        {academicYears ? (
                                            <PagedTiles items={academicYears} label="Academic years" render={renderAcademicYearTile} />
                                        ) : <p className="text-sm text-muted">Select a field first</p>}
                                    </div>
                                    {/* Courses */}
                                    <div>
                                        <h3 className="mb-2.5 text-[13px] font-semibold text-ink">Courses</h3>
                                        {coursesData ? (
                                            <PagedTiles items={coursesData} label="Courses" render={renderCourseTile} resetKey={academicYearId} />
                                        ) : <p className="text-sm text-muted">Select an academic year first</p>}
                                    </div>
                                </div>
                            ) : (
                                <p className="py-4 text-sm text-muted">Subjects for schools are chosen in the Add exam wizard.</p>
                            )}
                        </StepCard>
                    )}

                    {/* Step 3: Details */}
                    {step === 3 && (
                        <StepCard n={3} title="Exam details">
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <div className="md:col-span-2">
                                    <FormField label="Exam name" htmlFor="mixed-exam-name" required error={showStep3Errors && formdata.name.trim().length < 3 ? 'Enter an exam name (at least 3 characters).' : undefined}>
                                        <input id="mixed-exam-name" type="text" name="name" maxLength={NAME_MAX} value={formdata.name} onChange={handleInputChange} placeholder="e.g. Grade 12 national mock" className={MIX_INPUT} />
                                    </FormField>
                                </div>
                                <FormField label="Time (minutes)" htmlFor="mixed-exam-time" required hint="Students see a countdown; the exam auto-submits at zero." error={showStep3Errors && !(Number(formdata.given_time) > 0 && Number(formdata.given_time) <= 300) ? 'Time must be between 1 and 300 minutes.' : undefined}>
                                    <input id="mixed-exam-time" type="number" name="given_time" min={1} max={300} value={formdata.given_time} onChange={handleInputChange} className={MIX_INPUT} />
                                </FormField>
                                <FormField label="Exam tag" optional htmlFor="mixed-exam-tag">
                                    <select id="mixed-exam-tag" value={tagId} onChange={e => setTagId(e.target.value)} className={MIX_INPUT}>
                                        <option value="-1">No tag selected</option>
                                        {(tagsData || []).map((t: any) => <option key={t.id} value={String(t.id)}>{t.name}</option>)}
                                    </select>
                                </FormField>
                                <div className="md:col-span-2">
                                    <FormField label="Description" optional htmlFor="mixed-exam-desc">
                                        <textarea id="mixed-exam-desc" name="description" rows={3} value={formdata.description} onChange={handleInputChange} placeholder="Brief description" className={`${MIX_INPUT} min-h-[96px] resize-y leading-normal`} />
                                    </FormField>
                                </div>
                                {isHubAdmin && !nationalExamMode && targetLevel === 'college_university' && (
                                    <div className="md:col-span-2">
                                        <UniversityChoice id={s.universityId ?? null} name={s.universityName ?? ''}
                                            onChange={(id, name) => updateThis({ universityId: id, universityName: name })} />
                                    </div>
                                )}
                                {!nationalExamMode && role === 'superuser' && (
                                    <div className="md:col-span-2">
                                        <MixSwitch checked={isNational} label="Mark as national exam" hint="Links it to an EUEE paper" onChange={() => setIsNational(!isNational)} />
                                    </div>
                                )}
                                {isNational && !nationalExamMode && role === 'superuser' && (
                                    <div className="md:col-span-2">{nationalSelect}</div>
                                )}
                            </div>
                        </StepCard>
                    )}

                    {/* Step 4: Questions & Processing (merged) */}
                    {step === 4 && createdExamId && (
                        <div className="min-w-0 space-y-4">
                            {!pendingQuestions ? (
                                <>
                                    <div>
                                        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Step 4</p>
                                        <h2 className="mt-1 font-display text-[17px] font-bold tracking-[-0.01em] text-ink">{questionsExtracted ? 'Review questions' : 'Add questions'}</h2>
                                    </div>
                                    <ExamQuestionAddCard
                                        examId={createdExamId}
                                        onProceedToMeta={(qs: any[]) => updateThis({ pendingQuestions: qs, status: 'processing' })}
                                        onRegisterExtractFn={(fn: () => void) => setExtractQuestionsFn(() => fn)}
                                        onRegisterProceedFn={(fn: () => void) => setProceedToProcessingFn(() => fn)}
                                        onQuestionsExtracted={() => updateThis({ questionsExtracted: true })}
                                        isExtracting={isExtractingQuestions}
                                        setIsExtracting={setIsExtractingQuestions}
                                    />
                                </>
                            ) : (
                                <>
                                    <div>
                                        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Step 4</p>
                                        <h2 className="mt-1 font-display text-[17px] font-bold tracking-[-0.01em] text-ink">Processing {pendingQuestions.length} questions</h2>
                                    </div>
                                    <ExamProcessingPipeline
                                        examId={createdExamIds?.[0] || createdExamId}
                                        inputQuestions={pendingQuestions}
                                        courseIds={courseIds}
                                        // One grade's chapters and books, unless it is a national exam (all grades).
                                        gradeId={!isNational && gradeStateId !== '-1' ? gradeStateId : undefined}
                                        subjectId={subjectStateId !== '-1' ? subjectStateId : undefined}
                                        sessionId={sessionId}
                                        aiKnowledgeOnly={aiKnowledgeOnly}
                                        onSave={handleSaveQuestions}
                                    />
                                </>
                            )}
                        </div>
                    )}
                </div>

                {step !== 4 && (
                    <aside aria-label="Summary" className="flex min-w-0 flex-col gap-4 overflow-hidden rounded-[22px] bg-navy p-6 text-white shadow-card xl:sticky xl:top-4 xl:col-span-4">
                        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-on-navy-muted">Summary</p>
                        <dl className="space-y-2.5">
                            {summaryRows.map(r => (
                                <div key={r.label} className="flex items-baseline justify-between gap-4 text-[13px]">
                                    <dt className="flex-none text-on-navy-muted">{r.label}</dt>
                                    <dd className={`min-w-0 break-words text-right ${r.value ? 'font-semibold text-white' : 'text-on-navy-faint'}`}>{r.value ?? 'Not set'}</dd>
                                </div>
                            ))}
                        </dl>
                        <div className="flex flex-wrap items-center justify-center gap-2.5 border-t border-navy-3 pt-4">
                            {navButtons}
                        </div>
                        {blockerNote && <p className="-mt-1.5 text-center text-xs text-on-navy">{blockerNote}</p>}
                    </aside>
                )}
            </div>

            {step === 4 && (
                <div className="mt-[18px] flex flex-wrap items-center justify-between gap-3 rounded-[18px] border border-line bg-surface px-[18px] py-3.5 shadow-foot">
                    <span className="min-w-0 text-[13px] text-muted">Step 4 · Extract or add questions, then process them.</span>
                    <div className="flex flex-wrap items-center gap-2.5">{navButtons}</div>
                </div>
            )}
        </div>
    )
}

// Renders ALL sessions simultaneously, only showing the active one.
// This keeps pipeline hooks alive in background sessions.
const AllSessionsRenderer: React.FC = () => {
    const { state } = useExamSessions()
    return (
        <>
            {state.sessions.map(s => (
                <div
                    key={s.id}
                    style={s.id === state.activeId ? undefined : { display: 'none' }}
                >
                    <MixedExamInner sessionId={s.id} />
                </div>
            ))}
        </>
    )
}

/** Subtitle derived from the active session: its course count and, once imported, its question count. */
const MixedSubtitle: React.FC = () => {
    const { state } = useExamSessions()
    const active = state.sessions.find(sess => sess.id === state.activeId)
    const courses = active?.selectedCourses.length ?? 0
    const questions = active?.pendingQuestions?.length ?? 0
    const parts = [
        courses > 0 ? `${courses} course${courses === 1 ? '' : 's'}` : '',
        questions > 0 ? `${questions} question${questions === 1 ? '' : 's'}` : '',
    ].filter(Boolean)
    return <>{parts.length ? parts.join(' · ') : 'Combine several courses or a national exam into one exam. Questions are added after the details are saved.'}</>
}

const MixedExamHeader: React.FC = () => (
    <PageHeader
        eyebrow="Exams · Add exam · Mixed"
        title="Mixed exam"
        subtitle={<MixedSubtitle />}
        actions={
            <>
                <Link to="/exams/generate" className={LINK_BTN}><Sparkles size={18} strokeWidth={1.8} aria-hidden />Generate with AI</Link>
                <Link to="/exams/add-exam" className={LINK_BTN}>Single-subject exam</Link>
            </>
        }
    />
)

const MixedExamPage: React.FC = () => {
    const { role } = useAuth()
    const { data: userData, isLoading } = useUserInfo()
    if (!isLoading && !canUseMixedExam(role, userData)) {
        return (
            <div className="mx-auto min-h-screen max-w-7xl px-4 py-6">
                <PageHeader eyebrow="Exams · Add exam · Mixed" title="Mixed exam" />
                <PanelCard>
                    <EmptyState
                        icon={<AlertCircle size={22} strokeWidth={1.8} aria-hidden />}
                        title="Mixed exams are not available for schools"
                        body="Mixed exams combine national-exam or university courses. For your classes, write an exam for one subject, or let AI draft one from your chapters."
                        action={
                            <div className="flex flex-wrap justify-center gap-2.5">
                                <Link to="/exams/add-exam" className={LINK_BTN}>Create an exam</Link>
                                <Link to="/exams/generate" className={LINK_BTN}><Sparkles size={18} strokeWidth={1.8} aria-hidden />Generate with AI</Link>
                            </div>
                        }
                    />
                </PanelCard>
            </div>
        )
    }
    return (
        <div className="mx-auto min-h-screen max-w-7xl px-4 py-6">
            <ExamSessionProvider>
                <MixedExamHeader />
                <SessionTabs />
                <AllSessionsRenderer />
            </ExamSessionProvider>
        </div>
    )
}

export default MixedExamPage
